import { createStore } from 'react-sweet-state';
import { produce } from 'immer';
import { cloneDeep, omit, pick } from 'lodash';

import {
  assignVisualLanguageIdsToAssets,
  VISUAL_LANGUAGE_ASSET_FIELD,
} from 'src/data/visualLanguage';
import {
  createVisualLanguage,
  deleteVisualLanguage,
  queryCustomerVisualLanguages,
  queryWorkspaceVisualLanguagesWithAssets,
  updateVisualLanguage,
} from 'src/graphql/visualLanguage';
import { mergeEntityIntoEntityArray } from 'src/lib/collection';

const INITIAL_STATE = {
  customerVisualLanguages: [],
  loadingCustomerVisualLanguages: false,
  workspaceVisualLanguages: [],
  loadingWorkspaceVisualLanguages: false,
  defaultVisualLanguageId: null,

  savingVisualLanguage: false,
  deletingVisualLanguage: false,

  // IMPORTANT NOTE: do not manipulate this field directly - it should only be used as proxy between "visual language" and relevant stores
  // assets are loaded in one call, together with available visual languages and then get redistributed to relevant stores
  associatedAssets: {},
  associatedAssetsLoaded: false,
};

const PRIVATE_ACTIONS = {
  mergeVisualLanguageInCache:
    (itemToMerge) =>
    ({ getState, setState }) => {
      const { workspaceVisualLanguages: workspaceList, customerVisualLanguages: customerList } =
        getState();

      const newWorkspaceList = mergeEntityIntoEntityArray(workspaceList, itemToMerge, {
        leading: true,
      });
      const newCustomerList = mergeEntityIntoEntityArray(customerList, itemToMerge, {
        leading: true,
      });

      setState({
        workspaceVisualLanguages: newWorkspaceList,
        customerVisualLanguages: newCustomerList,
      });
    },

  deleteVisualLanguageFromCache:
    (itemIdToDelete) =>
    ({ getState, setState }) => {
      const { workspaceVisualLanguages: workspaceList, customerVisualLanguages: customerList } =
        getState();
      const newWorkspaceList = workspaceList.filter((item) => item.id !== itemIdToDelete);
      const newCustomerList = customerList.filter((item) => item.id !== itemIdToDelete);
      setState({
        workspaceVisualLanguages: newWorkspaceList,
        customerVisualLanguages: newCustomerList,
      });
    },
};

export const ACTIONS = {
  clearStore:
    () =>
    ({ setState }) => {
      setState(cloneDeep(INITIAL_STATE));
    },

  queryCustomerVisualLanguages:
    () =>
    async ({ setState }, { currentCustomerId }) => {
      setState({ loadingCustomerVisualLanguages: true });
      try {
        const items = await queryCustomerVisualLanguages({ id: currentCustomerId });
        setState({ customerVisualLanguages: items });
      } finally {
        setState({ loadingCustomerVisualLanguages: false });
      }
    },

  queryWorkspaceVisualLanguagesWithAssets:
    ({ workspaceId }, options) =>
    async ({ setState }, { currentCustomerId }) => {
      setState({ loadingWorkspaceVisualLanguages: true });
      try {
        // fetch visual languages along with associated assets
        const { visualLanguages, defaultVisualLanguageId } =
          await queryWorkspaceVisualLanguagesWithAssets(
            {
              customerId: currentCustomerId,
              workspaceId,
            },
            options
          );

        // split visual languages into actual entities and associated assets
        const { entities, associatedAssets } = splitToEntitiesAndAssociatedAssets(visualLanguages);
        const defaultEntity = pickDefaultEntity(entities, defaultVisualLanguageId);

        setState({
          workspaceVisualLanguages: entities,
          defaultVisualLanguageId: defaultEntity?.id,
          associatedAssets,
          associatedAssetsLoaded: true,
        });
      } finally {
        setState({ loadingWorkspaceVisualLanguages: false });
      }
    },

  createVisualLanguage:
    ({ workspaceId, name }) =>
    async ({ setState, dispatch }, { currentCustomerId }) => {
      setState({ savingVisualLanguage: true });
      try {
        const createdItem = await createVisualLanguage({
          customerId: currentCustomerId,
          workspaceId,
          name,
        });
        dispatch(PRIVATE_ACTIONS.mergeVisualLanguageInCache(createdItem));
        return createdItem;
      } finally {
        setState({ savingVisualLanguage: false });
      }
    },

  updateVisualLanguage:
    ({ id, name }) =>
    async ({ setState, dispatch }) => {
      setState({ savingVisualLanguage: true });
      try {
        const updatedItem = await updateVisualLanguage({
          id,
          name,
        });
        dispatch(PRIVATE_ACTIONS.mergeVisualLanguageInCache(updatedItem));
      } finally {
        setState({ savingVisualLanguage: false });
      }
    },

  deleteVisualLanguage:
    (itemId) =>
    async ({ setState, dispatch }) => {
      setState({ deletingVisualLanguage: true });
      try {
        await deleteVisualLanguage({ id: itemId });
        dispatch(PRIVATE_ACTIONS.deleteVisualLanguageFromCache(itemId));
      } finally {
        setState({ deletingVisualLanguage: false });
      }
    },

  isSavingVisualLanguage:
    () =>
    ({ getState }) => {
      return getState().savingVisualLanguage;
    },

  getCustomerVisualLanguages:
    () =>
    ({ getState }) => {
      return getState().customerVisualLanguages;
    },

  getWorkspaceVisualLanguages:
    () =>
    ({ getState }) => {
      return getState().workspaceVisualLanguages;
    },

  getDefaultVisualLanguage:
    () =>
    ({ getState }) => {
      const { defaultVisualLanguageId, workspaceVisualLanguages } = getState();
      return workspaceVisualLanguages.find((vl) => vl.id === defaultVisualLanguageId) || null;
    },
};

export const VisualLanguageStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'visual-language-store',
});

const splitToEntitiesAndAssociatedAssets = (visualLanguages) => {
  const assetFields = Object.values(VISUAL_LANGUAGE_ASSET_FIELD);

  return visualLanguages.reduce(
    (acc, item) => {
      const entity = omit(item, assetFields);
      const entityAssets = pick(item, assetFields);

      return produce(acc, (draft) => {
        draft.entities.push(entity);

        Object.entries(entityAssets).forEach(([assetField, assets]) => {
          if (!draft.associatedAssets[assetField]) {
            draft.associatedAssets[assetField] = [];
          }

          const assetsWithVisualLanguageId = assignVisualLanguageIdsToAssets(assets, item.id);
          draft.associatedAssets[assetField].push(...assetsWithVisualLanguageId);
        });
      });
    },
    { entities: [], associatedAssets: {} }
  );
};

const pickDefaultEntity = (entities, defaultId) => {
  const firstEntity = entities[0];
  if (!defaultId) {
    return firstEntity;
  }

  const defaultEntity = entities.find((vl) => vl.id === defaultId);
  return defaultEntity ? defaultEntity : firstEntity;
};
